import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, ShoppingCart as CartIcon, ArrowLeft } from 'lucide-react';

const CartPage = () => {
  const navigate = useNavigate();
  const { user, cart, removeFromCart } = useAuth();
  const [removing, setRemoving] = React.useState(null);

  const handleRemove = async (projectId) => {
    setRemoving(projectId);
    try {
      await removeFromCart(projectId);
    } catch (error) {
      alert('Erro ao remover item do carrinho');
    } finally {
      setRemoving(null);
    }
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.project_price, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    
    // Se não estiver logado, redirecionar para página de login/cadastro
    if (!user) {
      navigate('/entrar', { state: { from: '/carrinho' } });
      return;
    }
    
    // Se estiver logado, ir direto para o pagamento do carrinho
    navigate('/checkout', { state: { cart: cart, fromCart: true } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="hover:text-[#00A86B]"
          >
            <ArrowLeft size={20} className="mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-gray-800">Meu Carrinho</h1>
        </div>

        {cart.length === 0 ? (
          <Card className="p-12 text-center">
            <CartIcon size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Seu carrinho está vazio</h3>
            <p className="text-gray-500 mb-6">Explore nossos projetos e adicione ao carrinho!</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#00A86B] hover:bg-[#008c5a] text-white"
            >
              Ver Projetos
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <Card key={item.project_id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800">{item.project_name}</h3>
                      <div className="mt-2 space-y-1">
                        {item.selected_services.includes('arquitetonico') && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">• Projeto Arquitetônico</span>
                            <span className="text-gray-700 ml-4">R$ {formatCurrency(item.project_base_price || item.project_price)}</span>
                          </div>
                        )}
                        {item.selected_services.includes('interiores') && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">• Projeto de Interiores</span>
                            <span className="text-gray-700 ml-4">R$ {formatCurrency(item.project_interior_price || 1618.50)}</span>
                          </div>
                        )}
                        {item.selected_services.includes('rrt') && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">• RRT + Assessoria Técnica</span>
                            <span className="text-gray-700 ml-4">R$ {formatCurrency(item.project_rrt_price || 1300.00)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">Subtotal</p>
                        <p className="font-bold text-xl text-[#00A86B]">R$ {formatCurrency(item.project_price)}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemove(item.project_id)}
                        disabled={removing === item.project_id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                      >
                        <Trash2 size={16} className="mr-1" />
                        {removing === item.project_id ? 'Removendo...' : 'Excluir'}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Resumo</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Itens ({cart.length})</span>
                    <span>R$ {formatCurrency(calculateTotal())}</span>
                  </div>
                  
                  <div className="flex justify-between text-xl font-bold text-gray-800 pt-3 border-t">
                    <span>Total:</span>
                    <span>R$ {formatCurrency(calculateTotal())}</span>
                  </div>

                  <div className="text-sm text-gray-600 text-center pt-2">
                    em até 12x sem juros no cartão
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold"
                >
                  {!user ? 'Entrar e Finalizar Compra' : 'Finalizar Compra'}
                </Button>
                
                {!user && (
                  <p className="text-xs text-center text-gray-500 mt-2">
                    Você será redirecionado para fazer login
                  </p>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
